import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { buildVerificationDeckDoc, createPreviewBlobUrl, injectImagesIntoHTML } from '../../utils/slidePreview';
import { ChatInterface } from './ChatInterface';
import type { ChatMessage, Image } from '../../types';

interface SlideVerifyImageResult {
  alt: string;
  loaded: boolean;
  naturalWidth: number;
  naturalHeight: number;
  cssClass: string;
  alignmentOk: boolean;
  renderedWidth: number;
  renderedHeight: number;
}

export interface SlideVerifyResult {
  marpIndex: number;
  images: SlideVerifyImageResult[];
  unresolvedPlaceholders: string[];
  pass: boolean;
}

interface PendingFix {
  marpIndex: number;
  previewHTML: string; // full MARP HTML with the proposed fix — NOT yet saved to DB
}

interface Props {
  open: boolean;
  fullMarpHTML: string;
  images: Image[];
  /**
   * Generate a proposed fix and return the new full MARP HTML for preview.
   * Must NOT save to the source HTML / DB yet.
   */
  onGeneratePreview: (
    marpIndex: number,
    message: string,
    chatHistory: ChatMessage[],
    currentFullMarpHTML: string,
  ) => Promise<string>;
  /** Commit the pending fix: persist to DB and update the store. */
  onApplyFix: (marpIndex: number, previewHTML: string) => Promise<void>;
  /** Discard the pending fix: restore the original state in DB and the store. */
  onDiscardFix: (marpIndex: number) => Promise<void>;
  onProceed: () => void;
  onClose: () => void;
}

/** Read-only preview of a single MARP slide. Rebuilds the blob URL on every html/marpIndex change. */
const SlidePreviewPane: React.FC<{ html: string; images: Image[]; marpIndex: number }> = ({
  html, images, marpIndex,
}) => {
  const [blobUrl, setBlobUrl] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    if (!html) return;
    const { url } = createPreviewBlobUrl(html, images, marpIndex);
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setBlobUrl(url);
    return () => { URL.revokeObjectURL(url); urlRef.current = ''; setBlobUrl(''); };
  }, [html, images, marpIndex]);

  return (
    <Box sx={{ width: '100%', aspectRatio: '16/9', bgcolor: '#111', overflow: 'hidden', flexShrink: 0 }}>
      <iframe
        src={blobUrl || undefined}
        title={`slide-${marpIndex}-preview`}
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    </Box>
  );
};

function getIssues(r: SlideVerifyResult): string[] {
  const issues: string[] = [];
  r.images.forEach((img) => {
    const label = img.alt || img.cssClass || 'image';
    if (!img.loaded) issues.push(`"${label}" failed to load`);
    else if (img.renderedWidth === 0) issues.push(`"${label}" is not visible (0 px width)`);
    else if (!img.alignmentOk) issues.push(`"${label}" is misaligned — class "${img.cssClass}" not taking effect`);
  });
  r.unresolvedPlaceholders.forEach((id) =>
    issues.push(`Placeholder "${id}" was not replaced with an image`),
  );
  return issues;
}

export const SlideVerificationDialog: React.FC<Props> = ({
  open,
  fullMarpHTML,
  images,
  onGeneratePreview,
  onApplyFix,
  onDiscardFix,
  onProceed,
  onClose,
}) => {
  const [localFullMarpHTML, setLocalFullMarpHTML] = useState('');
  const [results, setResults] = useState<SlideVerifyResult[]>([]);
  const [total, setTotal] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [blobUrl, setBlobUrl] = useState('');
  const [selectedMarpIndex, setSelectedMarpIndex] = useState<number | null>(null);
  const [chatHistories, setChatHistories] = useState<Record<number, ChatMessage[]>>({});
  const [chatLoadingIdx, setChatLoadingIdx] = useState<number | null>(null);

  // Pending fix: proposed change visible in the preview pane but not yet committed.
  const [pendingFix, setPendingFix] = useState<PendingFix | null>(null);
  const [applyingFix, setApplyingFix] = useState(false);
  const [discardingFix, setDiscardingFix] = useState(false);

  const blobUrlRef = useRef('');
  const imagesRef = useRef(images);
  const hasAutoSelectedRef = useRef(false);
  imagesRef.current = images;

  const passCount = results.filter((r) => r.pass).length;
  const failCount = results.filter((r) => !r.pass).length;
  const allPass = scanComplete && failCount === 0;
  const progress = total > 0 ? (results.length / total) * 100 : 0;
  const isScanning = !scanComplete;
  const isBusy = chatLoadingIdx !== null || applyingFix || discardingFix;

  const selectedResult = results.find((r) => r.marpIndex === selectedMarpIndex) ?? null;
  const selectedIssues = selectedResult ? getIssues(selectedResult) : [];

  // Whether a fix is pending for the currently selected slide.
  const hasPendingFix = pendingFix !== null && pendingFix.marpIndex === selectedMarpIndex;

  // The HTML to show in the preview pane: pending preview if available, else committed.
  const previewHTML = (hasPendingFix && pendingFix) ? pendingFix.previewHTML : localFullMarpHTML;

  // ── Reset on open ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open || !fullMarpHTML) return;
    hasAutoSelectedRef.current = false;
    setLocalFullMarpHTML(fullMarpHTML);
    setResults([]);
    setTotal(0);
    setScanComplete(false);
    setSelectedMarpIndex(null);
    setChatHistories({});
    setChatLoadingIdx(null);
    setPendingFix(null);
    setApplyingFix(false);
    setDiscardingFix(false);
  }, [open, fullMarpHTML]);

  // ── Rebuild scan iframe whenever the committed HTML changes ──────────────────

  useEffect(() => {
    if (!localFullMarpHTML) return;
    setResults([]);
    setTotal(0);
    setScanComplete(false);

    const injected = injectImagesIntoHTML(localFullMarpHTML, imagesRef.current);
    const verifyHtml = buildVerificationDeckDoc(injected);
    const blob = new Blob([verifyHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = url;
    setBlobUrl(url);
  }, [localFullMarpHTML]);

  useEffect(
    () => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); },
    [],
  );

  // ── postMessages from scan iframe ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'slide-verify-progress') {
        const { result, total: t } = e.data as {
          result: SlideVerifyResult; checked: number; total: number;
        };
        setTotal(t);
        setResults((prev) => [...prev, result]);
      } else if (e.data?.type === 'slide-verify-complete') {
        setScanComplete(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Auto-select first failing slide on the initial scan only.
  useEffect(() => {
    if (!scanComplete || results.length === 0 || hasAutoSelectedRef.current) return;
    hasAutoSelectedRef.current = true;
    setSelectedMarpIndex((results.find((r) => !r.pass) ?? results[0]).marpIndex);
  }, [scanComplete, results]);

  // ── Chat: generate preview (no DB commit) ───────────────────────────────────

  const handleChatSend = useCallback(
    async (marpIndex: number, message: string) => {
      setChatLoadingIdx(marpIndex);
      const history = chatHistories[marpIndex] ?? [];
      const userMsg: ChatMessage = { role: 'user', content: message };
      setChatHistories((prev) => ({ ...prev, [marpIndex]: [...(prev[marpIndex] ?? []), userMsg] }));

      try {
        const newPreviewHTML = await onGeneratePreview(marpIndex, message, history, localFullMarpHTML);
        const aiMsg: ChatMessage = {
          role: 'assistant',
          content: 'Preview ready — review the slide and click Apply to save, or Discard to revert.',
        };
        setChatHistories((prev) => ({ ...prev, [marpIndex]: [...(prev[marpIndex] ?? []), aiMsg] }));
        setPendingFix({ marpIndex, previewHTML: newPreviewHTML });
      } catch (err) {
        const errMsg: ChatMessage = {
          role: 'assistant',
          content: err instanceof Error ? err.message : 'Failed to generate fix. Please try again.',
        };
        setChatHistories((prev) => ({ ...prev, [marpIndex]: [...(prev[marpIndex] ?? []), errMsg] }));
      } finally {
        setChatLoadingIdx(null);
      }
    },
    [chatHistories, localFullMarpHTML, onGeneratePreview],
  );

  // ── Apply: commit pending fix to DB ─────────────────────────────────────────

  const handleApply = useCallback(async () => {
    if (!pendingFix) return;
    setApplyingFix(true);
    try {
      await onApplyFix(pendingFix.marpIndex, pendingFix.previewHTML);
      setLocalFullMarpHTML(pendingFix.previewHTML); // triggers re-scan
      setPendingFix(null);
    } finally {
      setApplyingFix(false);
    }
  }, [pendingFix, onApplyFix]);

  // ── Discard: restore original from DB ───────────────────────────────────────

  const handleDiscard = useCallback(async () => {
    if (!pendingFix) return;
    setDiscardingFix(true);
    try {
      await onDiscardFix(pendingFix.marpIndex);
      setPendingFix(null);
    } finally {
      setDiscardingFix(false);
    }
  }, [pendingFix, onDiscardFix]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '85vh', maxHeight: 720, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Off-screen scan iframe */}
      {blobUrl && (
        <Box
          component="iframe"
          src={blobUrl}
          title="slide-verify-scan"
          sx={{
            position: 'fixed', top: -10000, left: -10000,
            width: 960, height: 540,
            border: 'none', visibility: 'hidden', pointerEvents: 'none',
          }}
        />
      )}

      {/* Title + progress bar */}
      <DialogTitle sx={{ pb: 0, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>Slide Verification</Typography>
          {isScanning && (
            <Chip
              size="small"
              icon={<CircularProgress size={10} color="inherit" />}
              label={total > 0 ? `${results.length} / ${total}` : 'Scanning…'}
              color="primary" variant="outlined"
              sx={{ height: 24, fontSize: '0.72rem' }}
            />
          )}
          <IconButton size="small" onClick={onClose} disabled={isScanning || isBusy}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant={total > 0 ? 'determinate' : 'indeterminate'}
          value={progress}
          color={scanComplete ? (allPass ? 'success' : 'warning') : 'primary'}
          sx={{ height: 3, mx: -3, borderRadius: 0 }}
        />
      </DialogTitle>

      {/* Three-column body */}
      <DialogContent sx={{ flex: 1, display: 'flex', p: 0, overflow: 'hidden', minHeight: 0 }}>

        {/* ── Col 1: slide list ── */}
        <Box sx={{
          width: 200, flexShrink: 0,
          borderRight: '1px solid', borderColor: 'divider',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', bgcolor: 'grey.50',
        }}>
          <Box sx={{
            px: 2, py: 1.25,
            borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
              Slides
            </Typography>
            {scanComplete && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip icon={<CheckCircleIcon sx={{ fontSize: '11px !important' }} />} label={passCount}
                  color="success" size="small"
                  sx={{ height: 18, fontSize: '0.68rem', '& .MuiChip-label': { px: 0.75 } }} />
                {failCount > 0 && (
                  <Chip icon={<ErrorIcon sx={{ fontSize: '11px !important' }} />} label={failCount}
                    color="error" size="small"
                    sx={{ height: 18, fontSize: '0.68rem', '& .MuiChip-label': { px: 0.75 } }} />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
            {results.map((r) => {
              const isSelected = selectedMarpIndex === r.marpIndex;
              const hasChatHistory = (chatHistories[r.marpIndex]?.length ?? 0) > 0;
              const hasPending = pendingFix?.marpIndex === r.marpIndex;
              return (
                <Box key={r.marpIndex} onClick={() => setSelectedMarpIndex(r.marpIndex)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 0.875, cursor: 'pointer',
                  borderLeft: '3px solid',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                  transition: 'background-color 0.15s',
                }}>
                  {r.pass
                    ? <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main', flexShrink: 0 }} />
                    : <ErrorIcon sx={{ fontSize: 15, color: 'error.main', flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap
                      sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 600 : 400 }}>
                      Slide {r.marpIndex + 1}
                    </Typography>
                    {r.images.length > 0 && (
                      <Typography variant="caption" color="text.disabled" noWrap display="block"
                        sx={{ fontSize: '0.68rem' }}>
                        {r.images.length} image{r.images.length !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                  {hasPending && (
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                  )}
                  {!hasPending && hasChatHistory && (
                    <AutoAwesomeIcon sx={{ fontSize: 11, color: 'primary.light', flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}

            {isScanning && Array.from({ length: Math.max(0, Math.min(4, (total || 6) - results.length)) }).map((_, i) => (
              <Box key={`skel-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.875 }}>
                <CircularProgress size={13} sx={{ flexShrink: 0, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.78rem' }}>
                  Scanning…
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Col 2: live preview + slide info + issues + apply/discard ── */}
        <Box sx={{
          width: 300, flexShrink: 0,
          borderRight: '1px solid', borderColor: 'divider',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', bgcolor: 'background.paper',
        }}>
          {!selectedResult || selectedMarpIndex === null ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.disabled', p: 3 }}>
              {isScanning
                ? <><CircularProgress size={28} thickness={3} /><Typography variant="body2">Scanning…</Typography></>
                : <><AutoAwesomeIcon sx={{ fontSize: 36 }} /><Typography variant="body2" textAlign="center">Select a slide</Typography></>}
            </Box>
          ) : (
            <>
              {/* Live preview — shows pending fix immediately, before any DB commit */}
              <SlidePreviewPane html={previewHTML} images={images} marpIndex={selectedMarpIndex} />

              {/* Pending fix banner */}
              {hasPendingFix && (
                <Box sx={{
                  px: 2, py: 1.25,
                  bgcolor: 'warning.50',
                  borderBottom: '1px solid', borderColor: 'warning.light',
                  flexShrink: 0,
                }}>
                  <Typography variant="caption" fontWeight={600} color="warning.dark" display="block" sx={{ mb: 0.75 }}>
                    Preview — not saved yet
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small" variant="outlined" color="error"
                      onClick={() => void handleDiscard()}
                      disabled={discardingFix || applyingFix}
                      sx={{ flex: 1, fontSize: '0.75rem', py: 0.5 }}
                    >
                      {discardingFix ? <CircularProgress size={12} /> : 'Discard'}
                    </Button>
                    <Button
                      size="small" variant="contained" color="primary"
                      onClick={() => void handleApply()}
                      disabled={applyingFix || discardingFix}
                      sx={{ flex: 1, fontSize: '0.75rem', py: 0.5 }}
                    >
                      {applyingFix ? <CircularProgress size={12} color="inherit" /> : 'Apply'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Slide info */}
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: selectedResult.images.length > 0 ? 0.5 : 0 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Slide {selectedResult.marpIndex + 1}
                  </Typography>
                  <Chip
                    size="small"
                    icon={selectedResult.pass ? <CheckCircleIcon /> : <ErrorIcon />}
                    label={selectedResult.pass ? 'Pass' : `${selectedIssues.length} issue${selectedIssues.length !== 1 ? 's' : ''}`}
                    color={selectedResult.pass ? 'success' : 'error'}
                    sx={{ height: 20, fontSize: '0.68rem' }}
                  />
                  {isScanning && <CircularProgress size={11} sx={{ ml: 'auto' }} />}
                </Box>
                {selectedResult.images.map((img, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                    {img.loaded
                      ? <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main', flexShrink: 0 }} />
                      : <ErrorIcon sx={{ fontSize: 12, color: 'error.main', flexShrink: 0 }} />}
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                      {img.alt || `Image ${i + 1}`}
                      {img.naturalWidth > 0 && ` · ${img.naturalWidth}×${img.naturalHeight}`}
                      {!img.alignmentOk && ' · ⚠ misaligned'}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Issues (reflect last committed scan) */}
              {selectedIssues.length > 0 && (
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'rgba(211,47,47,0.04)', flexShrink: 0, overflowY: 'auto', maxHeight: 130 }}>
                  <Typography variant="caption" fontWeight={700} color="error.main"
                    sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    Issues detected
                  </Typography>
                  {selectedIssues.map((issue, i) => (
                    <Typography key={i} variant="caption" display="block" color="error.dark"
                      sx={{ fontSize: '0.75rem', lineHeight: 1.8 }}>
                      • {issue}
                    </Typography>
                  ))}
                </Box>
              )}

              <Box sx={{ flex: 1 }} />
            </>
          )}
        </Box>

        {/* ── Col 3: AI chat ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {!selectedResult || selectedMarpIndex === null ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.disabled', p: 3 }}>
              <AutoAwesomeIcon sx={{ fontSize: 36 }} />
              <Typography variant="body2" textAlign="center">Select a slide to refine with AI</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{
                px: 2, py: 1,
                borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: 0.75,
                bgcolor: 'rgba(25,118,210,0.04)',
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={700} color="primary.main"
                  sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5, flex: 1 }}>
                  Refine with AI
                </Typography>
                {chatLoadingIdx === selectedMarpIndex && <CircularProgress size={12} />}
              </Box>

              <Box sx={{ flex: 1, overflow: 'hidden', p: 1.5, minHeight: 0 }}>
                <ChatInterface
                  key={selectedMarpIndex}
                  messages={chatHistories[selectedMarpIndex] ?? []}
                  onSend={(msg) => void handleChatSend(selectedMarpIndex, msg)}
                  // Lock chat while a fix is pending for this slide — apply or discard first.
                  isLoading={chatLoadingIdx === selectedMarpIndex || hasPendingFix || applyingFix || discardingFix}
                  placeholder={
                    hasPendingFix
                      ? 'Apply or discard the current preview before sending a new message'
                      : selectedIssues.length > 0
                        ? 'Describe a fix… e.g. "move image to the right half"'
                        : 'Describe changes… e.g. "make the image 40% smaller"'
                  }
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        {scanComplete && (
          <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
            <Chip icon={<CheckCircleIcon />} label={`${passCount} / ${total} passed`}
              color="success" size="small" variant={allPass ? 'filled' : 'outlined'} />
            {failCount > 0 && (
              <Chip icon={<ErrorIcon />} label={`${failCount} failed`} color="error" size="small" />
            )}
          </Box>
        )}
        <Button onClick={onClose} disabled={isScanning || isBusy}>Cancel</Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={onProceed}
          disabled={isScanning || isBusy}
          color={failCount > 0 ? 'warning' : 'primary'}
          sx={{ borderRadius: 2 }}
        >
          {failCount > 0 ? 'Proceed Anyway' : 'Proceed to Phase 4'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
